import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, Users, Wallet, Settings, Calculator, User as UserIcon,
} from "lucide-react";

// Static catalog: stable URLs (sidebar items)
export const TAB_ROUTES = [
  { path: "/", label: "Resumen", icon: LayoutDashboard, key: "dashboard" },
  { path: "/agenda", label: "Agenda", icon: CalendarDays, key: "agenda" },
  { path: "/pacientes", label: "Pacientes", icon: Users, key: "pacientes" },
  { path: "/caja", label: "Caja", icon: Wallet, key: "caja" },
  { path: "/contabilidad", label: "Contabilidad", icon: Calculator, key: "contabilidad" },
  { path: "/ajustes", label: "Ajustes", icon: Settings, key: "ajustes" },
];

const DYNAMIC_PATTERNS = [
  { pattern: "/pacientes/:id", label: "Paciente", icon: UserIcon, baseKey: "patient" },
];

function describeTab(pathname) {
  const stat = TAB_ROUTES.find((r) => r.path === pathname);
  if (stat) return { id: stat.path, path: pathname, label: stat.label, icon: stat.icon };
  for (const p of DYNAMIC_PATTERNS) {
    const m = matchPath({ path: p.pattern }, pathname);
    if (m) {
      const id = pathname; // unique per param
      return { id, path: pathname, label: p.label, icon: p.icon, params: m.params };
    }
  }
  return null;
}

const TabsContext = createContext(null);

export function TabsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState(() => [{ id: "/", path: "/", label: "Resumen", icon: LayoutDashboard }]);
  const [activeId, setActiveId] = useState("/");

  // Sync URL → ensure tab exists & active
  useEffect(() => {
    const desc = describeTab(location.pathname);
    if (!desc) return;
    setTabs((prev) => {
      const exists = prev.find((t) => t.id === desc.id);
      if (exists) {
        // keep latest path/params
        return prev.map((t) => (t.id === desc.id ? { ...t, ...desc } : t));
      }
      return [...prev, desc];
    });
    setActiveId(desc.id);
  }, [location.pathname]);

  const openTab = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  const closeTab = useCallback((id) => {
    setTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === id);
      if (idx === -1 || prev.length <= 1) return prev;
      const next = prev.filter((t) => t.id !== id);
      // si cierra activa → activa la anterior (o la siguiente)
      if (activeId === id) {
        const fallback = prev[idx - 1] || prev[idx + 1] || next[0];
        if (fallback) navigate(fallback.path);
      }
      return next;
    });
  }, [activeId, navigate]);

  const moveTab = useCallback((fromIdx, toIdx) => {
    setTabs((prev) => {
      if (fromIdx === toIdx || toIdx < 0 || toIdx >= prev.length) return prev;
      const arr = prev.slice();
      const [m] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, m);
      return arr;
    });
  }, []);

  const value = useMemo(() => ({ tabs, activeId, openTab, closeTab, moveTab }), [tabs, activeId, openTab, closeTab, moveTab]);
  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

export const useTabs = () => useContext(TabsContext);
